import { IDataSyncModel } from 'Data/ViewModels/IDataSyncModel';
import WebApi from 'Lib/WebApi';
import { Epic, ofType } from 'redux-observable';
import { concat, of } from 'rxjs';
import { concatMap, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { actions } from '.';
import { SyncStatus, syncTarget } from '../../Core/legacyDataSync';
import { addErrorNotice, callApi, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import { getDefaultWorksheetStatusPatch, getWorksheetStatusPatch } from '../../Helpers/worksheetHelpers';
import { callApiGetTests, callApiGetWorksheet, callApiPutWorksheet } from './apiCalls';
import { RequestAction } from './types';

export const init: Epic = (action$) =>
  action$.pipe(
    ofType(actions.init.toString()),
    mergeMap(() => of(actions.refresh()))
  );

export const refreshOnQueryChanges: Epic = (action$) =>
  action$.pipe(
    ofType(actions.setQueryValue.toString()),
    filter(({ payload: { sync = undefined } = {} }) => !!sync), // only when sync is set to true
    mergeMap(() => of(actions.refresh()))
  );

export const refresh: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.refresh.toString()),
    switchMap(() => {
      // Ignore refresh actions if we aren't ready to query for worksheets
      return state$.value.worksheetGrid.status.initialised && state$.value.worksheetGrid.status.queryReady
        ? callApiGetTests(state$.value.worksheetGrid.worksheetType, state$.value.worksheetGrid.query.params).pipe(
            mergeMap((worksheets) =>
              of(
                actions.refreshResolve({
                  worksheets,
                  sync: true,
                })
              )
            ),
            catchWithAuthErrors((err: any) =>
              of(actions.refreshReject(), addErrorNotice('Failed to retrieve worksheets for grid entry.', err))
            )
          )
        : of(actions.refreshResolve({ worksheets: [] }));
    })
  );

// Epic to synchronise a specific worksheet (responsible for keeping the server up-to-date with the client changes).
// This runs like a service, continually (once at a time) updating the server state to look like the client state.
export const sync: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.synchroniseWorksheet.toString()),
    // TODO: Syncs for the same worksheetId should be debounced. See QP-3972.
    concatMap(({ payload: { force = undefined, worksheetId = undefined } = {} }) =>
      syncTarget(state$.value.worksheetGrid.worksheets[worksheetId], force, ({ updateModel }: any) => {
        const worksheetTypeName = state$.value.worksheetGrid.worksheetType;
        if (updateModel) {
          // Skip conflict resolution on server because the web app doesn't support it for dry mass entry.
          (updateModel as IDataSyncModel).syncVersionData = null;
          return callApiPutWorksheet(worksheetTypeName, { Body: updateModel }, { worksheetId });
        } else {
          return callApiGetWorksheet(worksheetTypeName, worksheetId);
        }
      })
    ),
    mergeMap(({ target, status, syncPatch, error, recoveryError }: any) => {
      const handleSyncError = (error: any) => {
        return of(
          actions.syncError({
            worksheetId: target.syncModel.data.Id,
            syncPatch,
          })
        );
      };

      const epicActionsOut = [];
      if (error) {
        if (error.code === 460) return handleSyncError(error);
        epicActionsOut.push(addErrorNotice('Failed to update worksheet.', error));
      }
      if (recoveryError)
        epicActionsOut.push(
          addErrorNotice(
            'Something went really wrong while attempting to retrieve worksheet, try refreshing the page.',
            recoveryError
          )
        );
      epicActionsOut.push(
        actions.synchroniseWorksheetComplete({
          worksheetId: target.syncModel.data.Id,
          sync: status === SyncStatus.success,
          syncPatch,
        })
      );
      return of(...epicActionsOut);
    })
  );

export const updateWorksheetStatusOnSyncComplete: Epic = (action$) =>
  action$.pipe(
    ofType(actions.synchroniseWorksheetComplete.toString()),
    mergeMap(({ payload: { worksheetId = undefined } = {} }) => of(actions.updateWorksheetStatus({ worksheetId })))
  );

export const updateWorksheetStatus: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.updateWorksheetStatus.toString()),
    switchMap(({ payload: { worksheetId = undefined } = {} }) => {
      if (!state$.value.worksheetGrid.status.initialised) {
        // Reset calculated status to default
        return of(actions.patchWorksheetStatus({ worksheetId, status: getDefaultWorksheetStatusPatch() }));
      }

      const {
        value: {
          worksheetGrid: {
            worksheets: {
              [worksheetId]: { syncModel: worksheet, status },
            },
          },
          user,
        },
      } = state$;

      return of(
        actions.patchWorksheetStatus({
          worksheetStatusPatches: [
            {
              worksheetId,
              status: getWorksheetStatusPatch(worksheet, user, status),
            },
          ],
        })
      );
    })
  );

export const updateAllWorksheetStatusOnRefreshResolve: Epic = (action$) =>
  action$.pipe(
    ofType(actions.refreshResolve.toString()),
    mergeMap(() => of(actions.updateAllWorksheetStatus()))
  );

export const updateAllWorksheetStatus: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.updateAllWorksheetStatus.toString()),
    filter(() => state$.value.worksheetGrid.status.initialised),
    switchMap(() => {
      const {
        value: {
          worksheetGrid: { worksheets },
          user,
        },
      } = state$;

      const worksheetStatusPatches = Object.entries(worksheets).map(([worksheetId, value]) => {
        const { syncModel: worksheet, status } = value as any;
        return { worksheetId, status: getWorksheetStatusPatch(worksheet, user, status) };
      });

      return of(actions.patchWorksheetStatus({ worksheetStatusPatches }));
    })
  );

/**
 * Posts a request to an endpoint other than the main get/post endpoints for the worksheet.
 * Should be used for test-specific endpoints that trigger further actions.
 * Worksheet will be synced after request is complete
 **/
export const postRequest: Epic = (action$) =>
  action$.pipe(
    ofType(actions.postRequest.toString()),
    switchMap((action: RequestAction) => {
      const { worksheetId, requestId, endpoint } = action.payload;
      return callApi(WebApi[endpoint].post({}, { worksheetId: worksheetId })).pipe(
        switchMap(() =>
          concat(
            // Emit forced sync action
            of(actions.synchroniseWorksheet({ force: true, worksheetId })),
            // Listen for the syncResolve action, then resolve
            action$.pipe(
              ofType(actions.synchroniseWorksheetComplete.toString()),
              filter((action) => action.payload.worksheetId === worksheetId),
              take(1),
              switchMap(() => of(actions.postRequestResolve({ requestId })))
            )
          )
        ),
        catchWithAuthErrors((err: any) =>
          of(actions.postRequestReject({ requestId }), addErrorNotice('Failed to make request.', err))
        )
      );
    })
  );

export const syncErrorResolveRetry: Epic = (action$) =>
  action$.pipe(
    ofType(actions.syncErrorResolve.toString()),
    filter(({ payload: { retry = false } = {} }) => !!retry),
    mergeMap((action: RequestAction) => of(actions.synchroniseWorksheet({ worksheetId: action.payload.worksheetId })))
  );

export const syncErrorResolve: Epic = (action$) =>
  action$.pipe(
    ofType(actions.syncErrorResolve.toString()),
    filter(({ payload: { retry = false } = {} }) => !retry),
    mergeMap((action: RequestAction) => of(actions.synchroniseWorksheetComplete(action.payload)))
  );

export const getDefaultBalance: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.getDefaultBalance.toString()),
    switchMap(({ payload: { laboratoryId = undefined } = {} }) => {
      return callApi(WebApi.laboratory.defaultBalance.get({ laboratoryUUID: laboratoryId })).pipe(
        mergeMap((response: any) =>
          of(
            actions.getDefaultBalanceResolve({
              balance: response.Body,
            })
          )
        ),
        catchWithAuthErrors((err: any) =>
          of(actions.getDefaultBalanceReject(), addErrorNotice('Failed to load default balance.', err))
        )
      );
    })
  );

export const setDefaultBalance: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.postDefaultBalance.toString()),
    mergeMap(({ payload: { balanceUUID = undefined, laboratoryId = undefined } = {} }) => {
      return callApi(
        WebApi.laboratory.defaultBalance.post(
          {
            Body: {
              DefaultBalance: balanceUUID,
            },
          },
          { laboratoryUUID: laboratoryId }
        )
      ).pipe(
        mergeMap((response: any) => of(actions.postDefaultBalanceResolve())),
        catchWithAuthErrors((err: any) =>
          of(actions.postDefaultBalanceReject(), addErrorNotice('Failed to set default balance.', err))
        )
      );
    })
  );
